/* eslint-disable react/destructuring-assignment */
import React, { useContext, useEffect, useState } from 'react'
import { I18n, Amplify, Hub } from '@aws-amplify/core'
import { Auth } from '@aws-amplify/auth'
import { Loader } from '@tumelo/shared'
import { useAppDispatch, useAppSelector } from '../application/store'
import { getQueryVariable } from '../utils/getQueryVariable'
import { ConfigAws } from '../config/ConfigAws'
import { completeAuthAction, setAuthState } from '../application/features/auth'
import { useLogout } from '../application/hooks'
import { LoggerContext } from '../utils/loggerContext'
import { GenericError } from '../components/Error'
import { SignUp } from './CustomSignUp'
import { ConfirmSignUp } from './CustomConfirmSignUp'
import { SignIn } from './CustomSignIn'
import { ForgotPassword } from './CustomForgotPassword'
import { ConfirmSignIn } from './CustomConfirmSignIn'

// TODO:
// Pull redux state on each page
// Handle threading & figure out what needs storing
// Sign in -> Sign up = maintian email password
// Sign up -> Maintain help hint
// Sign in -> confirm sign up = maintain user (email?)
// Sign up -> confirm sign up = maintain user (email?)
// Auto sign in after confirm account
// Forgot password = maintain email
const START_SIGN_IN_QUERY_KEY = 'startSignIn'
const startNewSession = getQueryVariable(START_SIGN_IN_QUERY_KEY) === '1'

export const configure = (configuration: ConfigAws) => {
  // v.federationTarget = "COGNITO_USER_POOLS"
  const config = {
    Auth: {
      identityPoolId: configuration.aws_cognito_identity_pool_id,
      region: configuration.aws_cognito_region,
      userPoolId: configuration.aws_user_pools_id,
      userPoolWebClientId: configuration.aws_user_pools_web_client_id,
      mandatorySignIn: true,
      oauth:
        configuration.oauth !== undefined && configuration.oauth.aws_cognito_domain
          ? {
              domain: configuration.oauth.aws_cognito_domain, // `testingdevdeploymentstuff.auth.${verifiedConfiguration?.aws_cognito_region}.amazoncognito.com`,
              redirectSignIn: window.location.origin, // 'http://localhost:3000',
              redirectSignOut: `${window.location.origin}${startNewSession ? '?startSignIn=1' : ''}`, // 'http://localhost:3000',
              scope: ['profile', 'openid', 'aws.cognito.signin.user.admin'],
              responseType: 'code',
            }
          : undefined,
    },
  }
  Amplify.configure(config)
}

const authLabels = {
  en: {
    'Confirm Sign Up': 'We have sent an email containing a confirmation code to the following address',
    'Lost your code? ':
      "It can take a couple of minutes for your code to arrive. If you have problems, please check we have the correct email address. Don't forget to check your spam/junk box. Still no joy? ",
    Username: 'Email',
    'Back to Sign In': 'Go to Sign In',
    'Create account': 'Sign Up',
    'Enter your username': 'Enter your email address',
    'User does not exist': 'We do not recognise this email address. Please sign up below.',
    'Custom auth lambda trigger is not configured for the user pool.': 'You need to put in your password.',
    'Incorrect username or password.': 'Incorrect email or password. Please check your details again.',
    'An account with the given email already exists.':
      'An account with that email address already exists. Please check your email address is correct or sign in below.',
    'Invalid verification code provided, please try again.':
      'Your verification code is not correct, please try again or ask for another code below.',
    'PreSignUp failed with error email_address_not_supported.':
      'Sorry, this email address is not allowed. Please use another address.',
    'A failure occurred when returning to the Cognito Hosted UI':
      'Something went wrong. Please try again in a few minutes, if the problem persists contact support@tumelo.com',
  },
}

I18n.setLanguage('en')
I18n.putVocabularies(authLabels)

const MODEL_PORTFOLIO_ID_QUERY_KEY = 'modelPortfolioId'
const IS_NEW_USER_QUERY_KEY = 'newUser'
const END_LAST_SESSION_QUERY_KEY = 'endLastSession'

// useForceNewSession ends any old sessions if "endLastSession" query variable is set
const useForceNewSession = () => {
  const { logout } = useLogout()
  const { logError } = useContext(LoggerContext)
  const [status, setStatus] = useState<'pending' | 'error' | 'complete'>('pending')
  const endLastSession = getQueryVariable(END_LAST_SESSION_QUERY_KEY) === '1'

  useEffect(() => {
    const forceNewSession = async () => {
      try {
        if (endLastSession) {
          await logout({ withSingleLogout: false })
        }
        setStatus('complete')
      } catch (e) {
        setStatus('error')
        logError(e)
      }
    }
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    forceNewSession()
  }, [endLastSession, setStatus, logout, logError])

  return { status }
}

const useInitAuthState = () => {
  const dispatch = useAppDispatch()
  const authState = useAppSelector((state) => state.auth.authState)
  const logger = useContext(LoggerContext)
  const modelPortfolioID = getQueryVariable(MODEL_PORTFOLIO_ID_QUERY_KEY)
  const isNewUser = getQueryVariable(IS_NEW_USER_QUERY_KEY)?.toLowerCase() === 'true'
  const targetPage = modelPortfolioID || isNewUser ? 'signUp' : 'signIn'

  React.useEffect(() => {
    if (authState === 'not-initialized') {
      Auth.currentAuthenticatedUser()
        .then((user) => {
          if (user) {
            dispatch(setAuthState('authenticated'))
          } else {
            dispatch(setAuthState(targetPage))
          }
        })
        .catch((err) => {
          if (err === 'The user is not authenticated') {
            dispatch(setAuthState(targetPage))
          } else {
            dispatch(setAuthState('error'))
            logger.logError(err)
          }
        })
    }
  }, [authState, targetPage, logger, dispatch])

  React.useEffect(() => {
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      const { event, message, data } = payload
      if (event === 'cognitoHostedUI_failure') {
        logger.logError(new Error(`${event}: ${message} \n data: ${JSON.stringify(data)}`))
        dispatch(completeAuthAction({ code: event, message: message ?? '' }))
      }
    })
    return unsubscribe
  }, [dispatch, logger])
}

export const AuthWrapper: React.FC<React.PropsWithChildren> = ({ children }) => {
  const authState = useAppSelector((state) => state.auth.authState)
  useInitAuthState()
  const { status } = useForceNewSession()

  // Handle force new session logic first
  if (status === 'pending') {
    return <Loader />
  }
  if (status === 'error') {
    return <GenericError onClick={() => window.location.reload()} />
  }

  // Handle each possible auth state
  if (authState === 'authenticated') {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>
  }
  if (authState === 'not-initialized' || authState === 'federatedSignIn' || authState === 'signOut') {
    return <Loader />
  }
  if (authState === 'confirmSignIn') {
    return <ConfirmSignIn />
  }
  if (authState === 'confirmSignUp') {
    return <ConfirmSignUp />
  }
  if (authState === 'forgotPassword') {
    return <ForgotPassword />
  }
  if (authState === 'signUp') {
    return <SignUp />
  }
  if (authState === 'error') {
    return <GenericError onClick={() => window.location.reload()} />
  }

  return <SignIn />
}
